import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import Chip from 'tio-ui/components/chip';
import type { SectionId } from '../../../utils/tas/program-builder/sections';
import type { ComponentLike } from '@glint/template';

type IconComponent = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

export interface ReadoutRailItem {
  id: SectionId;
  label: string;
  icon: IconComponent;
  isActive: boolean;
}

interface ReadoutRailSignature {
  Args: {
    items: ReadoutRailItem[];
    programName: string;
    typeShort: string;
    /** Launched / Suspended / Draft chip under the program context header. */
    stateLabel: string;
    stateIntent: 'success' | 'warning' | 'default';
    onSelect: (id: SectionId) => void;
  };
  Element: HTMLElement;
}

/**
 * Passive left rail for the read-only program template view: program context
 * header (name + type chip) over the applicable section tabs. Unlike the admin
 * builder's rail there is no completion meter and no per-section status — the
 * tabs only switch which read-out section is shown.
 */
const ReadoutRail: TOC<ReadoutRailSignature> = <template>
  <nav
    data-test-readout-rail
    class="rounded-xl border tio-border-default tio-bg-surface px-3 py-4"
    ...attributes
  >
    <div class="px-2">
      <div class="tio-text-overline tio-fg-muted">{{t "program_builder.readout.overline"}}</div>
      <div class="mt-0.5 tio-text-base-semibold {{unless @programName 'italic tio-fg-muted'}}">
        {{if @programName @programName (t "program_builder.readout.untitled_program")}}
      </div>
      <div class="mt-2 tio-row-snug flex-wrap">
        {{#if @typeShort}}
          <Chip @intent="primary" @appearance="faded" @size="sm">{{@typeShort}}</Chip>
        {{/if}}
        <Chip
          data-test-readout-state
          @intent={{@stateIntent}}
          @appearance="faded"
          @size="sm"
        >{{@stateLabel}}</Chip>
      </div>
    </div>

    <div class="my-3 h-px tio-bg-sunken"></div>

    <div class="tio-col-flush gap-0.5">
      {{#each @items as |item|}}
        <button
          type="button"
          data-test-readout-rail-tab={{item.id}}
          aria-current={{if item.isActive "page"}}
          class="tio-row rounded-lg px-3 py-2 text-left transition-colors
            {{if
              item.isActive
              'tio-bg-brand-soft tio-fg-brand'
              'tio-fg-secondary hover:tio-bg-sunken'
            }}"
          {{on "click" (fn @onSelect item.id)}}
        >
          {{#let item.icon as |Icon|}}
            <Icon @size={{18}} class="shrink-0" />
          {{/let}}
          <span class="flex-1 text-sm">{{item.label}}</span>
        </button>
      {{/each}}
    </div>
  </nav>
</template>;

export default ReadoutRail;
